import { ISnippet } from '@principle-theorem/editor';
import { INotification } from '@principle-theorem/notifications';
import {
  ISODateType,
  Region,
  Timezone,
  WithRef,
} from '@principle-theorem/shared';
import { IAccountCredit } from './account-credit/account-credit';
import { IApiKey } from './api-key';
import { IAppointment } from './appointment/appointment';
import {
  ISchedulingEvent,
  ISchedulingEventReason,
} from './appointment/cancellation';
import { IChecklistItem } from './appointment/checklist-item';
import { IRole } from './auth/role';
import { IAutomation, IAutomationConfiguration } from './automation/automation';
import { AnyAutomation } from './automation/automations';
import { IBrand } from './brand';
import { IClinicalChart } from './clinical-charting/clinical-chart';
import { IConditionConfiguration } from './clinical-charting/conditions/condition-configuration';
import { IClinicalNote } from './clinical-charting/core/clinical-note';
import { IFeeSchedule } from './clinical-charting/fees/fee-schedule/fee-schedule';
import { IMultiTreatmentConfiguration } from './clinical-charting/treatment/multi-treatment-configuration';
import { ITreatmentConfiguration } from './clinical-charting/treatment/treatment-configuration';
import { ITreatmentPlan } from './clinical-charting/treatment/treatment-plan';
import { ITreatmentStep } from './clinical-charting/treatment/treatment-step';
import { ITreatmentTemplate } from './clinical-charting/treatment/treatment-template';
import { IContact } from './contact/contact';
import {
  ICustomFormConfiguration,
  ICustomFormContent,
} from './custom-forms/custom-form-config';
import { IPatientForm } from './custom-forms/patient-form';
import { ICalendarEvent, ICalendarEventSchedule } from './event/calendar-event';
import { IScheduleSummary } from './event/schedule-summary';
import { IFollowUp } from './follow-up/follow-up';
import { EmailMessage } from './interaction/email-message';
import { IInteractionV2 } from './interaction/interaction';
import { SMSConversation, SMSMessage } from './interaction/sms-message';
import { IInvoice } from './invoice/invoice';
import { ILabJob } from './lab-job/lab-job';
import { ILab } from './lab/lab';
import { IMarketingCampaign } from './marketing/campaign';
import { IMarketingCampaignEntry } from './marketing/campaign-entry';
import { IMarketingCampaignRun } from './marketing/campaign-run';
import { IMarketingCampaignStep } from './marketing/campaign-step';
import { IMedia } from './media/media';
import { IOrganisation } from './organisation/organisation';
import { IWebhook } from './partner/webhook';
import { IPatientPhoneNumber } from './patient-phone-number';
import { IPatient } from './patient/patient';
import { IPatientDocument } from './patient/patient-document';
import { IPatientMetadataDisplay } from './patient/patient-metadata';
import { IReferralSourceConfiguration } from './patient/referral-source';
import { IPractice } from './practice/practice';
import { IPrescriptionItem } from './prescription/prescription';
import { IProduct } from './product/product';
import {
  IReferral,
  IReferralClinicalNote,
  IReferralInteraction,
  IReferralMedia,
  IReferralMessage,
  IReferralNote,
} from './referral/referral';
import { ICustomReport } from './reports/custom-report';
import { IStaffer } from './staffer/staffer';
import {
  ISterilisationCycle,
  ISterilisationCycleType,
  ISterilisationEquipment,
  ISterilisationMachine,
  ISterilisationPack,
  ISterilisationRecord,
} from './sterilisation/sterilisation-record';
import { ITag } from './tag/tag';
import { IRecurringTaskConfiguration } from './task/recurring-task';
import { ITask } from './task/task';
import { ITeam } from './team/team';
import { ITemplateDefinition } from './templating/template-definition';
import { IFundingRequest } from './transaction/funding-request/funding-request';
import { IManualTransactionType } from './transaction/manual-transaction-type';
import { ITransaction } from './transaction/transaction';
import { ITreatmentCategory } from './treatment-category';
import { IUser } from './user';

export type ICreateDemoWorkspaceRequest = Pick<
  IOrganisation,
  'name' | 'region'
> & {
  seedFileName: string;
  timezone: Timezone;
  practiceName?: string;
  userName?: string;
  userEmail?: string;
};

export interface IExportedDemoOrganisation {
  anchorDate?: ISODateType;
  brands: IExportedDemoBrand[];
  feeSchedules: WithRef<IFeeSchedule>[];
  organisation: WithRef<IOrganisation>;
  roles: WithRef<IRole>[];
  users: WithRef<IUser>[];
  webhooks: WithRef<IWebhook>[];
  apiKeys: WithRef<IApiKey>[];
}

export interface IExportedCustomForm {
  customForm: WithRef<ICustomFormConfiguration>;
  content: WithRef<ICustomFormContent>[];
}

export interface IExportedDemoBrand {
  appointmentTags: WithRef<ITag>[];
  automationConfigurations: WithRef<IAutomationConfiguration>[];
  brand: WithRef<IBrand>;
  calendarEvents: WithRef<ICalendarEvent>[];
  cancellationReasons: WithRef<ISchedulingEventReason>[];
  conditionConfigurations: WithRef<IConditionConfiguration>[];
  contacts: WithRef<IContact>[];
  customFormConfigurations: IExportedCustomForm[];
  customReports: WithRef<ICustomReport>[];
  emails: WithRef<EmailMessage>[];
  eventTags: WithRef<ITag>[];
  feeSchedules: WithRef<IFeeSchedule>[];
  labs: WithRef<ILab>[];
  marketingCampaigns: IExportedDemoMarketingCampaign[];
  manualTransactionTypes: WithRef<IManualTransactionType>[];
  multiTreatmentConfigurations: WithRef<IMultiTreatmentConfiguration>[];
  patientMetadataDisplays: WithRef<IPatientMetadataDisplay>[];
  patientNoteTags: WithRef<ITag>[];
  patientPhoneNumbers: WithRef<IPatientPhoneNumber>[];
  patientTags: WithRef<ITag>[];
  patients: IExportedDemoPatient[];
  practices: IExportedDemoPractice[];
  prescriptionItems: WithRef<IPrescriptionItem>[];
  products: WithRef<IProduct>[];
  referrals: IExportedDemoReferral[];
  referralSources: WithRef<IReferralSourceConfiguration>[];
  smsMessages: WithRef<SMSMessage>[];
  smsConversations: WithRef<SMSConversation>[];
  snippets: WithRef<ISnippet>[];
  staff: IExportedDemoStaffer[];
  sterilisationCycleTypes: WithRef<ISterilisationCycleType>[];
  sterilisationEquipment: WithRef<ISterilisationEquipment>[];
  sterilisationMachines: WithRef<ISterilisationMachine>[];
  sterilisationPacks: WithRef<ISterilisationPack>[];
  teams: WithRef<ITeam>[];
  templates: WithRef<ITemplateDefinition>[];
  treatmentCategories: WithRef<ITreatmentCategory>[];
  treatmentConfigurations: WithRef<ITreatmentConfiguration>[];
  treatmentTemplates: WithRef<ITreatmentTemplate>[];
}

export interface IExportedDemoTreatmentPlan {
  treatmentPlan: WithRef<ITreatmentPlan>;
  treatmentSteps: IExportedDemoTreatmentStep[];
}

export interface IExportedDemoTreatmentStep {
  automations: WithRef<IAutomation<AnyAutomation>>[];
  treatmentStep: WithRef<ITreatmentStep>;
}

export interface IExportedDemoInvoice {
  invoice: WithRef<IInvoice>;
  transactions: WithRef<ITransaction>[];
}

export interface IExportedDemoPatient {
  appointments: IExportedDemoAppointment[];
  clinicalCharts: WithRef<IClinicalChart>[];
  clinicalNotes: WithRef<IClinicalNote>[];
  credits: WithRef<IAccountCredit>[];
  history: WithRef<IInteractionV2>[];
  invoices: IExportedDemoInvoice[];
  media: WithRef<IMedia>[];
  patient: WithRef<IPatient>;
  patientDocuments: WithRef<IPatientDocument>[];
  submittedForms: WithRef<IPatientForm>[];
  treatmentPlans: IExportedDemoTreatmentPlan[];
}

export interface IExportedDemoPractice {
  contacts: WithRef<IContact>[];
  feeSchedules: WithRef<IFeeSchedule>[];
  labJobs: WithRef<ILabJob>[];
  practice: WithRef<IPractice>;
  products: WithRef<IProduct>[];
  recurringTaskConfigurations: WithRef<IRecurringTaskConfiguration>[];
  scheduleSummaries: WithRef<IScheduleSummary>[];
  sterilisationRecords: WithRef<ISterilisationRecord>[];
  sterilisationCycles: WithRef<ISterilisationCycle>[];
  tags: WithRef<ITag>[];
  tasks: WithRef<ITask>[];
  fundingRequests: WithRef<IFundingRequest<unknown>>[];
}

export interface IExportedDemoStaffer {
  conditionConfigurations: WithRef<IConditionConfiguration>[];
  feeSchedules: WithRef<IFeeSchedule>[];
  multiTreatmentConfigurations: WithRef<IMultiTreatmentConfiguration>[];
  notifications: WithRef<INotification>[];
  rosterSchedules: WithRef<ICalendarEventSchedule>[];
  snippets: WithRef<ISnippet>[];
  staffer: WithRef<IStaffer>;
  treatmentConfigurations: WithRef<ITreatmentConfiguration>[];
}

export interface IExportedDemoAppointment {
  appointment: WithRef<IAppointment>;
  appointmentInteractions: WithRef<IInteractionV2>[];
  checklists: WithRef<IChecklistItem>[];
  followUps: WithRef<IFollowUp>[];
  schedulingEvents: WithRef<ISchedulingEvent>[];
}

export interface IExportedDemoMarketingCampaign {
  campaign: WithRef<IMarketingCampaign>;
  steps: WithRef<IMarketingCampaignStep>[];
  entries: WithRef<IMarketingCampaignEntry>[];
  runs: WithRef<IMarketingCampaignRun>[];
}

export interface IExportedDemoReferral {
  referral: WithRef<IReferral>;
  messages: WithRef<IReferralMessage>[];
  media: WithRef<IReferralMedia>[];
  interactions: WithRef<IReferralInteraction>[];
  clinicalNotes: WithRef<IReferralClinicalNote>[];
  notes: WithRef<IReferralNote>[];
}

export const REGION_DEFAULT_TIMEZONE: Record<Region, Timezone> = {
  [Region.Australia]: Timezone.AustraliaSydney,
  [Region.NewZealand]: Timezone.NZ,
};
